import { useState } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Truncate } from '@re-dev/react-truncate'
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import { useReselector } from '@/utils/sharedHooks'
import { TransactionOrSplit } from '@/reducers/admin/allTransactions.slice'

import { Text, Button, Link, Separator, Alert, Tooltip, FileInput } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { NotesInput } from './NotesInput'
import { DetailsTooltip } from './DetailsTooltip'
import { selectReceiptsForTransaction } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { destroySingleUserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { UploadDocumentType } from '@/constants/businessConstants'
import { userUploadDocument } from '@/actions/awsActions'
import { getCurrentUser } from '@/selectors/user.selectors'

interface NotesPanelProps {
  transaction: TransactionOrSplit
  toggleFileModal: () => void
  splitIndex?: number
  parentId?: number
  setPreventClose: (value: boolean) => void
}

export const NotesPanel = ({
  transaction,
  toggleFileModal: _toggleFileModal,
  splitIndex,
  parentId,
  setPreventClose,
}: NotesPanelProps) => {
  const [error, setError] = useState('')
  const [uploadError, setUploadError] = useState('')
  const transactionIdForDocs = parentId || transaction.id

  const documents = useReselector(
    selectReceiptsForTransaction,
    transactionIdForDocs,
    splitIndex
  )
  const user = useReselector(getCurrentUser)
  const dispatch = useAppDispatch()

  const deleteFile = async (fileId: number) => {
    setError('')
    const deleted = await destroySingleUserDocument(fileId)(dispatch)
    if (!deleted) setError('Error deleting your receipt.')
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]

    if (!file) {
      setUploadError('Please select a file to upload.')
      return
    }

    if (!user) {
      setUploadError('User not found. Please try again.')
      return
    }

    setUploadError('')
    setPreventClose(true)

    try {
      const payload = {
        file,
        user: { id: user.id, uuid: user.uuid },
        documentType: UploadDocumentType.RECEIPT,
        transactionId: transactionIdForDocs,
        splitIndex,
      }

      const docRes = await userUploadDocument(payload)(dispatch)

      if (docRes && 'userDocument' in docRes) {
        // File uploaded successfully, documents will be updated via selector
        setPreventClose(false)
      } else {
        setUploadError('Failed to upload file. Please try again.')
        setPreventClose(false)
      }
    } catch (error) {
      console.error('File upload error:', error)
      setUploadError('Failed to upload file. Please try again.')
      setPreventClose(false)
    }
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex-1 grow mt-2">
        <NotesInput transaction={transaction} />
      </div>

      <Separator />

      <div className="flex-1 grow">
        <div className="flex flex-row items-center">
          <Text variant={TypographyStyles.LABEL_MD} color={TextColors.MUTED}>
            Receipt
          </Text>
          <DetailsTooltip label="Receipt">
            <Text>Uploading a receipt is optional but recommended.</Text>
          </DetailsTooltip>
        </div>

        {documents?.map((doc) => (
          <div className="mb-2" key={doc.id}>
            <Tooltip>
              <Tooltip.Trigger asChild>
                <Link href={doc?.signedUrl || ''} newPage>
                  <Truncate width={350}>{doc?.fileDescription}</Truncate>
                </Link>
              </Tooltip.Trigger>
              <Tooltip.Content>
                <Text variant={TypographyStyles.BODY_SM}>
                  {doc?.fileDescription}
                </Text>
              </Tooltip.Content>
            </Tooltip>
            <Button
              variant="discreet"
              onClick={() => deleteFile(doc.id)}
              iconLeft={faTrashCan}
              size="square"
              className="ml-2"
            />
            <br />
          </div>
        ))}

        <FileInput
          label="Upload"
          onChange={handleFileChange}
          accept=".pdf,.jpg,.jpeg,.png,.gif,.tiff,.bmp"
        />
      </div>

      {error && (
        <Alert variant="error" className="w-full mt-4" description={error} />
      )}

      {uploadError && (
        <Alert
          variant="error"
          className="w-full mt-4"
          description={uploadError}
        />
      )}
    </div>
  )
}
